import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Check, ArrowRight, MessageCircle } from "lucide-react";

export default function RSVP() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({
    event_id: "", full_name: "", nickname: "", whatsapp: "", age: "",
    city: "", church: "", instagram: "", both_days: null, has_companion: false,
    companion_name: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/events/public").then((r) => {
      const open = r.data.filter((e) => e.registrations_open && (e.event_type || "edicao") === "edicao");
      setEvents(open);
      const next = open.find((e) => e.is_next) || open[0];
      if (next) setForm((f) => ({ ...f, event_id: next.id }));
    });
  }, []);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.event_id) return toast.error("Selecione um evento");
    if (form.both_days === null || form.both_days === undefined) return toast.error("Informe se vai participar dos dois dias ou apenas do primeiro");
    setSubmitting(true);
    try {
      const payload = { ...form, age: parseInt(form.age || "0", 10) };
      const { data } = await api.post("/participants", payload);
      setResult(data);
      toast.success("Presença confirmada!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao confirmar presença");
    } finally { setSubmitting(false); }
  };

  const copyId = () => {
    navigator.clipboard.writeText(result.registration_id);
    setCopied(true);
    toast.success("ID copiado!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (result) {
    return (
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-20 text-center">
        <div className="ci-card p-8 sm:p-12 animate-fade-up">
          <div className="text-6xl mb-4">💙</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black">PRESENÇA CONFIRMADA!</h1>
          <p className="text-slate-300 mt-4">
            Estamos esperando você para viver mais uma experiência inesquecível no Conexão Inefável.
          </p>
          <div className="mt-8">
            <div className="label">Seu ID de inscrição</div>
            <div className="mt-2 inline-flex items-center gap-3 bg-blue-500/10 border border-blue-500/40 rounded-xl px-5 py-3">
              <span data-testid="rsvp-id" className="font-mono text-xl text-blue-300 text-glow-blue font-bold">{result.registration_id}</span>
              <button data-testid="rsvp-copy-btn" onClick={copyId} className="text-blue-300 hover:text-white">
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
          </div>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <button data-testid="rsvp-home-btn" onClick={() => nav("/")} className="btn-ghost">Voltar ao início</button>
            <button onClick={() => { setResult(null); setForm({ ...form, full_name: "", whatsapp: "", age: "", city: "" }); }} className="btn-primary">
              Nova inscrição <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
      <div className="label">Confirmar Presença</div>
      <h1 className="font-display text-4xl sm:text-5xl font-black mt-2">Garanta sua vaga.</h1>
      <p className="text-slate-400 mt-3">Preencha o formulário e nos vemos lá. ✨</p>

      <form onSubmit={submit} className="ci-card p-6 sm:p-8 mt-10 space-y-5" data-testid="rsvp-form">
        <div>
          <label className="label block mb-2">Edição</label>
          <select data-testid="rsvp-event-select" value={form.event_id} onChange={(e) => setF("event_id", e.target.value)} required className="ci-input">
            <option value="">Selecione um encontro</option>
            {events.map((e) => (
              <option key={e.id} value={e.id}>{e.edition_number}ª Edição — {e.name}</option>
            ))}
          </select>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label block mb-2">Nome completo *</label>
            <input data-testid="rsvp-fullname" required value={form.full_name} onChange={(e) => setF("full_name", e.target.value)} className="ci-input" />
          </div>
          <div>
            <label className="label block mb-2">Como prefere ser chamado</label>
            <input data-testid="rsvp-nickname" value={form.nickname} onChange={(e) => setF("nickname", e.target.value)} className="ci-input" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label block mb-2">WhatsApp *</label>
            <input data-testid="rsvp-whatsapp" required placeholder="(11) 99999-9999" value={form.whatsapp} onChange={(e) => setF("whatsapp", e.target.value)} className="ci-input" />
          </div>
          <div>
            <label className="label block mb-2">Idade *</label>
            <input data-testid="rsvp-age" required type="number" min="10" max="100" value={form.age} onChange={(e) => setF("age", e.target.value)} className="ci-input" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label block mb-2">Cidade *</label>
            <input data-testid="rsvp-city" required value={form.city} onChange={(e) => setF("city", e.target.value)} className="ci-input" />
          </div>
          <div>
            <label className="label block mb-2">Igreja (opcional)</label>
            <input data-testid="rsvp-church" value={form.church} onChange={(e) => setF("church", e.target.value)} className="ci-input" />
          </div>
        </div>
        <div>
          <label className="label block mb-2">Instagram (opcional)</label>
          <input data-testid="rsvp-instagram" placeholder="@seu_perfil" value={form.instagram} onChange={(e) => setF("instagram", e.target.value)} className="ci-input" />
        </div>
        <div>
          <label className="label block mb-2">Participação *</label>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              { v: true, t: "Os dois dias" },
              { v: false, t: "Apenas o primeiro dia" },
            ].map((o) => (
              <button type="button" key={String(o.v)}
                      data-testid={`rsvp-days-${o.v ? "both" : "first"}`}
                      onClick={() => setF("both_days", o.v)}
                      className={`p-4 rounded-xl border text-sm font-semibold transition-all ${form.both_days === o.v ? "border-[#1E90FF] bg-[#1E90FF]/10 text-white" : "border-white/10 bg-black/40 text-slate-300 hover:border-white/30"}`}>
                {o.t}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input data-testid="rsvp-companion" type="checkbox" checked={form.has_companion} onChange={(e) => setF("has_companion", e.target.checked)} className="w-5 h-5 accent-blue-400" />
            <span className="text-sm">Vou levar acompanhante</span>
          </label>
        </div>
        {form.has_companion && (
          <div>
            <label className="label block mb-2">Nome do acompanhante</label>
            <input data-testid="rsvp-companion-name" value={form.companion_name} onChange={(e) => setF("companion_name", e.target.value)} className="ci-input" />
          </div>
        )}
        <div>
          <label className="label block mb-2">Observações</label>
          <textarea data-testid="rsvp-notes" rows={3} value={form.notes} onChange={(e) => setF("notes", e.target.value)} className="ci-input resize-none" />
        </div>
        <button data-testid="rsvp-submit-btn" type="submit" disabled={submitting} className="btn-primary w-full disabled:opacity-50">
          {submitting ? "Confirmando..." : <>Confirmar minha inscrição <MessageCircle size={18} /></>}
        </button>
      </form>
    </div>
  );
}
